import {
  VERTICAL_OVERRIDE_THUMBNAIL_COUNT,
  VERTICAL_THUMBNAIL_HEIGHT_RATIO,
} from './constants';

export interface ThumbnailMetadata {
  position: number;
  width: number;
  height: number;
  timeInSeconds: number;
}

export interface VideoMetadata {
  duration: number;
  isVertical: boolean;
  width: number;
  height: number;
}

export interface VideoSelection {
  startTime: number;
  endTime: number;
  duration: number;
}

export type TrimAction = 'trim-left' | 'trim-right' | 'move' | null;

/**
 * Compute the effective thumbnails-per-second for a given video.
 *  1. Duration-based adjustment — short videos get higher density so the
 *     filmstrip isn't mostly empty (≤10 s → 1.0 TPS, ≤20 s → 0.75 TPS).
 *  2. Adaptive max cap — ensures we never exceed `maxThumbnails` total,
 *     scaling TPS down proportionally for longer videos.
 */
interface GetEffectiveThumbnailsPerSecondArgs {
  duration: number;
  thumbnailsPerSecond: number;
  maxThumbnails: number;
  thumbnailCountOverride?: number | null;
}
export function getEffectiveThumbnailsPerSecond({
  duration,
  thumbnailsPerSecond,
  maxThumbnails,
  thumbnailCountOverride = null,
}: GetEffectiveThumbnailsPerSecondArgs): number {
  if (thumbnailCountOverride !== null) {
    return thumbnailCountOverride / duration;
  }

  let adjustedThumbnailsPerSecond = thumbnailsPerSecond;
  if (duration <= 10) {
    adjustedThumbnailsPerSecond = 1.0;
  } else if (duration <= 20) {
    adjustedThumbnailsPerSecond = 0.75;
  }

  const adaptiveMaxThumbnails = Math.min(
    maxThumbnails,
    Math.max(10, Math.ceil(duration))
  );
  const maxThumbnailsPerSecond = adaptiveMaxThumbnails / duration;

  return Math.min(adjustedThumbnailsPerSecond, maxThumbnailsPerSecond);
}

interface CalculateThumbnailsArgs {
  duration: number;
  stripHeight: number;
  thumbnailsPerSecond: number;
  containerWidth: number;
  isVertical?: boolean;
}

export function calculateThumbnails({
  duration,
  stripHeight,
  thumbnailsPerSecond,
  containerWidth,
  isVertical,
}: CalculateThumbnailsArgs): ThumbnailMetadata[] {
  if (containerWidth <= 0 || thumbnailsPerSecond <= 0) return [];

  const thumbnailInterval = 1 / thumbnailsPerSecond;
  const count = Math.ceil(duration / thumbnailInterval);
  if (count <= 0) return [];

  const thumbnailWidth = containerWidth / count;

  const thumbnails: ThumbnailMetadata[] = [];

  for (let i = 0; i < count; i++) {
    thumbnails.push({
      position: i * thumbnailWidth,
      width: thumbnailWidth,
      height: isVertical
        ? VERTICAL_THUMBNAIL_HEIGHT_RATIO * thumbnailWidth
        : stripHeight,
      timeInSeconds: i * thumbnailInterval,
    });
  }
  return thumbnails;
}

export function pixelsToTime(
  pixels: number,
  duration: number,
  stripWidth: number
): number {
  return (pixels / stripWidth) * duration;
}

/** Format time as MM:SS for video player display. */
export function formatTimeDisplay(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
    .toString()
    .padStart(2, '0')}`;
}

/** Constrain left trim: clamp to [0, endTime-min] and [endTime-max, endTime-min]. */
export function constrainTrimLeft(
  newStartTime: number,
  endTime: number,
  minDuration: number = 1,
  maxDuration: number = 5
): number {
  let constrained = Math.max(0, newStartTime);
  constrained = Math.min(constrained, endTime - minDuration);
  constrained = Math.max(constrained, endTime - maxDuration);
  return constrained;
}

/** Constrain right trim: clamp to [startTime+min, videoDuration] and [startTime+min, startTime+max]. */
export function constrainTrimRight(
  newEndTime: number,
  startTime: number,
  videoDuration: number,
  minDuration: number = 1,
  maxDuration: number = 5
): number {
  let constrained = Math.min(videoDuration, newEndTime);
  constrained = Math.max(constrained, startTime + minDuration);
  constrained = Math.min(constrained, startTime + maxDuration);
  return constrained;
}

export function roundToPrecision(value: number, precision: number = 2): number {
  const multiplier = Math.pow(10, precision);
  return Math.round(value * multiplier) / multiplier;
}
